import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoginModal from '../../auth/components/LoginModal.web';
import i18next from '../../base/i18n/i18next';
import Footer from './Footer.web';
import LandingNavbar from './LandingNavbar';
const RTL_LANGUAGES = ['fa', 'ar', 'he'];
const SECTION_IDS = [
    'collection',
    'usage',
    'camera',
    'content',
    'thirdParty',
    'security',
    'retention',
    'rights',
    'children',
    'changes',
    'contact'
];
const PrivacyPage = () => {
    const { t } = useTranslation();
    const [activeId, setActiveId] = useState('collection');
    useEffect(() => {
        const currentLang = i18next.language || 'fa';
        const isRtl = RTL_LANGUAGES.includes(currentLang);
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        document.documentElement.lang = currentLang;
        document.title = t('privacyPage.documentTitle');
        const handleLanguageChanged = (lng) => {
            document.documentElement.dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
            document.documentElement.lang = lng;
            document.title = t('privacyPage.documentTitle');
        };
        i18next.on('languageChanged', handleLanguageChanged);
        return () => {
            i18next.off('languageChanged', handleLanguageChanged);
        };
    }, [t]);
    useEffect(() => {
        const headings = SECTION_IDS
            .map(id => document.getElementById(`privacy-${id}`))
            .filter((el) => Boolean(el));
        if (!headings.length) {
            return;
        }
        const observer = new IntersectionObserver(entries => {
            const visible = entries
                .filter(entry => entry.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
            if (visible[0]?.target.id) {
                setActiveId(visible[0].target.id.replace('privacy-', ''));
            }
        }, {
            rootMargin: '-120px 0px -55% 0px',
            threshold: [0, 0.2, 0.5, 1]
        });
        headings.forEach(heading => observer.observe(heading));
        return () => observer.disconnect();
    }, []);
    const handleTocClick = useCallback((event, id) => {
        event.preventDefault();
        setActiveId(id);
        document.getElementById(`privacy-${id}`)?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
        });
        history.replaceState(null, '', `#privacy-${id}`);
    }, []);
    const renderList = (keys) => (React.createElement("ul", { className: 'privacy-list' }, keys.map(key => (React.createElement("li", { key: key }, t(key))))));
    return (React.createElement("div", { className: 'static-page privacy-page' },
        React.createElement("a", { className: 'privacy-skip', href: '#privacy-article' }, t('privacyPage.skipToContent')),
        React.createElement(LandingNavbar, null),
        React.createElement(LoginModal, null),
        React.createElement("main", { className: 'static-page-content privacy-page-content' },
            React.createElement("header", { className: 'privacy-hero' },
                React.createElement("h1", { className: 'static-page-title' }, t('privacyPage.title')),
                React.createElement("p", { className: 'privacy-updated' }, t('privacyPage.lastUpdated', { date: t('privacyPage.lastUpdatedDate') })),
                React.createElement("p", { className: 'privacy-intro' }, t('privacyPage.intro'))),
            React.createElement("div", { className: 'privacy-layout' },
                React.createElement("nav", { "aria-label": t('privacyPage.tocLabel'), className: 'privacy-toc' },
                    React.createElement("p", { className: 'privacy-toc-title' }, t('privacyPage.tocLabel')),
                    React.createElement("ol", { className: 'privacy-toc-list' }, SECTION_IDS.map((id, index) => (React.createElement("li", { key: id },
                        React.createElement("a", { "aria-current": activeId === id ? 'location' : undefined, className: `privacy-toc-link ${activeId === id ? 'is-active' : ''}`, href: `#privacy-${id}`, onClick: event => handleTocClick(event, id) },
                            React.createElement("span", { className: 'privacy-toc-index' }, index + 1),
                            React.createElement("span", null, t(`privacyPage.toc.${id}`)))))))),
                React.createElement("article", { className: 'privacy-article', id: 'privacy-article' },
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-collection' },
                        React.createElement("h2", null, t('privacyPage.sections.collection.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.intro')),
                        React.createElement("h3", null, t('privacyPage.sections.collection.account.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.account.intro')),
                        renderList([
                            'privacyPage.sections.collection.account.items.name',
                            'privacyPage.sections.collection.account.items.phone',
                            'privacyPage.sections.collection.account.items.avatar',
                            'privacyPage.sections.collection.account.items.userId',
                            'privacyPage.sections.collection.account.items.auth'
                        ]),
                        React.createElement("h3", null, t('privacyPage.sections.collection.meetings.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.meetings.intro')),
                        renderList([
                            'privacyPage.sections.collection.meetings.items.id',
                            'privacyPage.sections.collection.meetings.items.time',
                            'privacyPage.sections.collection.meetings.items.members',
                            'privacyPage.sections.collection.meetings.items.settings',
                            'privacyPage.sections.collection.meetings.items.connection',
                            'privacyPage.sections.collection.meetings.items.reports'
                        ]),
                        React.createElement("p", null, t('privacyPage.sections.collection.meetings.recordingNote')),
                        React.createElement("h3", null, t('privacyPage.sections.collection.av.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.av.intro')),
                        renderList([
                            'privacyPage.sections.collection.av.items.camera',
                            'privacyPage.sections.collection.av.items.mic'
                        ]),
                        React.createElement("p", null, t('privacyPage.sections.collection.av.note')),
                        React.createElement("h3", null, t('privacyPage.sections.collection.screenShare.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.screenShare.body')),
                        React.createElement("h3", null, t('privacyPage.sections.collection.technical.title')),
                        React.createElement("p", null, t('privacyPage.sections.collection.technical.intro')),
                        renderList([
                            'privacyPage.sections.collection.technical.items.device',
                            'privacyPage.sections.collection.technical.items.os',
                            'privacyPage.sections.collection.technical.items.app',
                            'privacyPage.sections.collection.technical.items.ip',
                            'privacyPage.sections.collection.technical.items.network',
                            'privacyPage.sections.collection.technical.items.errors',
                            'privacyPage.sections.collection.technical.items.usage'
                        ])),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-usage' },
                        React.createElement("h2", null, t('privacyPage.sections.usage.title')),
                        React.createElement("p", null, t('privacyPage.sections.usage.intro')),
                        renderList([
                            'privacyPage.sections.usage.items.account',
                            'privacyPage.sections.usage.items.calls',
                            'privacyPage.sections.usage.items.meetings',
                            'privacyPage.sections.usage.items.participants',
                            'privacyPage.sections.usage.items.notifications',
                            'privacyPage.sections.usage.items.security',
                            'privacyPage.sections.usage.items.abuse',
                            'privacyPage.sections.usage.items.bugs',
                            'privacyPage.sections.usage.items.support',
                            'privacyPage.sections.usage.items.improve'
                        ]),
                        React.createElement("p", null, t('privacyPage.sections.usage.minimization'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-camera' },
                        React.createElement("h2", null, t('privacyPage.sections.camera.title')),
                        React.createElement("p", null, t('privacyPage.sections.camera.body')),
                        React.createElement("p", null, t('privacyPage.sections.camera.control'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-content' },
                        React.createElement("h2", null, t('privacyPage.sections.content.title')),
                        React.createElement("p", null, t('privacyPage.sections.content.private')),
                        React.createElement("p", null, t('privacyPage.sections.content.thirdParty')),
                        React.createElement("p", null, t('privacyPage.sections.content.recording'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-thirdParty' },
                        React.createElement("h2", null, t('privacyPage.sections.thirdParty.title')),
                        React.createElement("p", null, t('privacyPage.sections.thirdParty.noSale')),
                        React.createElement("p", null, t('privacyPage.sections.thirdParty.intro')),
                        renderList([
                            'privacyPage.sections.thirdParty.items.infra',
                            'privacyPage.sections.thirdParty.items.storage',
                            'privacyPage.sections.thirdParty.items.security',
                            'privacyPage.sections.thirdParty.items.analytics',
                            'privacyPage.sections.thirdParty.items.ops'
                        ]),
                        React.createElement("p", null, t('privacyPage.sections.thirdParty.limit')),
                        React.createElement("p", null, t('privacyPage.sections.thirdParty.legal'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-security' },
                        React.createElement("h2", null, t('privacyPage.sections.security.title')),
                        React.createElement("p", null, t('privacyPage.sections.security.body')),
                        React.createElement("p", null, t('privacyPage.sections.security.limit'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-retention' },
                        React.createElement("h2", null, t('privacyPage.sections.retention.title')),
                        React.createElement("p", null, t('privacyPage.sections.retention.body')),
                        React.createElement("p", null, t('privacyPage.sections.retention.deletion')),
                        React.createElement("p", null, t('privacyPage.sections.retention.residual'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-rights' },
                        React.createElement("h2", null, t('privacyPage.sections.rights.title')),
                        React.createElement("p", null, t('privacyPage.sections.rights.intro')),
                        renderList([
                            'privacyPage.sections.rights.items.access',
                            'privacyPage.sections.rights.items.correct',
                            'privacyPage.sections.rights.items.delete',
                            'privacyPage.sections.rights.items.restrict',
                            'privacyPage.sections.rights.items.revoke'
                        ]),
                        React.createElement("p", null, t('privacyPage.sections.rights.contact'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-children' },
                        React.createElement("h2", null, t('privacyPage.sections.children.title')),
                        React.createElement("p", null, t('privacyPage.sections.children.body'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-changes' },
                        React.createElement("h2", null, t('privacyPage.sections.changes.title')),
                        React.createElement("p", null, t('privacyPage.sections.changes.body')),
                        React.createElement("p", null, t('privacyPage.sections.changes.notice')),
                        React.createElement("p", null, t('privacyPage.sections.changes.acceptance'))),
                    React.createElement("section", { className: 'privacy-section', id: 'privacy-contact' },
                        React.createElement("h2", null, t('privacyPage.sections.contact.title')),
                        React.createElement("p", null, t('privacyPage.sections.contact.intro')),
                        React.createElement("dl", { className: 'privacy-contact' },
                            React.createElement("div", { className: 'privacy-contact-row' },
                                React.createElement("dt", null, t('privacyPage.sections.contact.serviceLabel')),
                                React.createElement("dd", null, t('privacyPage.sections.contact.serviceValue'))),
                            React.createElement("div", { className: 'privacy-contact-row' },
                                React.createElement("dt", null, t('privacyPage.sections.contact.phoneLabel')),
                                React.createElement("dd", null,
                                    React.createElement("a", { dir: 'ltr', href: 'tel:02188107613' }, "021-88107613"))),
                            React.createElement("div", { className: 'privacy-contact-row' },
                                React.createElement("dt", null, t('privacyPage.sections.contact.websiteLabel')),
                                React.createElement("dd", null,
                                    React.createElement("a", { href: 'https://didarr.ir', rel: 'noopener noreferrer', target: '_blank' }, "didarr.ir"))),
                            React.createElement("div", { className: 'privacy-contact-row' },
                                React.createElement("dt", null, t('privacyPage.sections.contact.supportLabel')),
                                React.createElement("dd", null,
                                    React.createElement("a", { href: '/support' }, t('privacyPage.sections.contact.supportValue'))))),
                        React.createElement("p", { className: 'privacy-closing' }, t('privacyPage.closing')))))),
        React.createElement(Footer, null)));
};
export default PrivacyPage;
